import moment from 'moment'
import {range, omit, chunk, chain, orderBy} from 'lodash'
import fetch, {createPublic} from 'helpers/command'
import {createPlanSection} from 'helpers/planning'
import {indexEditionParents} from 'components/standards-selector/domain/planning-pdf.cjs'
import {subdomainResource, inViewer} from 'helpers/url' 
import {handleSettings} from './openfire'
import {getVideos} from 'external/youtube'

function whenInPage (pages, fetch) {
  return (context, ...args)  => {
    return pages.indexOf(context.page) >= 0 && fetch(context, ...args)
  }
}

function delayedSetState(defaultDelay, fetch) {
  let timeoutId = 0
  return async (context, options = {}) => {
    const {delay = defaultDelay} = options
    const data = await fetch(context)
    clearTimeout(timeoutId)
    timeoutId = setTimeout(context.setState, delay, {...data})
  }
}

export function CatalogueSearch (context) {
  let searching = context.page == 'catalogue' && context.searching == 'lessons'
  searching = searching || (inPlanning(context) && context.section == 'add-lessons' && context.activeTab == 'catalogue')
  if (searching) {
    const loading = catalogueSearch => ({...context.loading, catalogueSearch})
    context.setState({loading: loading(true)})
    delayedSetState(0, fetch('catalog','search', {
      mapParameters: ({query}) => query,
      mapResponse: catalogueSearchResults => ({catalogueSearchResults, loading: loading(false)})
    }))(context)
  }
}

export function CatalogueSubjects (context) {
  if (context.page == 'catalogue' && !context.subject) {
    delayedSetState(0, fetch('catalog','subjects', {
      mapParameters: ({languageId, query}) => ({language: query.languageId || languageId}),
      mapResponse: catalogueSubjects => ({catalogueSubjects})
    }))(context)
  }
}

export function CatalogueCourses (context) {
  if (context.page == 'catalogue' && context.subject) {
    delayedSetState(0, fetch('catalog','courses', {
      mapParameters: ({languageId, query, subject}) => ({subject, language: query.languageId || languageId}),
      mapResponse: catalogueCourses => ({catalogueCourses})
    }))(context)
  }
}

export function Units (context) {
  const course = ['assessment-bank','question-bank'].indexOf(context.tab) > -1
    ? context.query.course
    : context.course
  if (course) {
    context.setState({units: []})
    delayedSetState(0, fetch('teacher', 'units', {
      mapParameters: ({languageId, query}) => ({course, language: query.languageId || languageId}),
      mapResponse: units => ({units})
    }))(context)
  }
}

export function Lessons (context) {

  const unit = ['assessment-bank', 'question-bank'].indexOf(context.tab) > -1
    ? context.query.unit
    : context.course && context.unit
      ? `${context.course}U${context.unit}`
      : false
  if (unit) {
    context.setState({lessons: []})
    delayedSetState(10, fetch('teacher', 'unitLessons', {
      mapParameters: ({languageId, query}) => ({unit, language: query.languageId || languageId}),
      mapResponse: lessons => ({lessons})
    }))(context)
  }
}

export function MyCourses (context) { 
  if (context.page == 'my-courses' || context.page == 'assessment' || context.page == 'quiz' || context.page == 'catalogue') {
    context.setState({myCourses: []})
    delayedSetState(0, fetch('teacher', 'myCourses', {
      mapParameters: ({languageId: language}) => ({language}),
      mapResponse: myCourses => ({myCourses})
    }))(context)
  }
}

export const NewsItems = whenInPage (['dashboard'],
  delayedSetState(700, createPublic('news', {
    mapParameters: ({language: {id: language}}) => ({language}),
    mapResponse: (news) => ({news})
})))

export const ScheduledDays = whenInPage (['dashboard'],
  delayedSetState(0, fetch('teacher', 'ScheduledDays', {
    mapParameters: (() => undefined),
    mapResponse: scheduledDays => ({scheduledDays})
})))

export const RecentActivity = whenInPage (['dashboard'],
  delayedSetState(850, fetch('teacher', 'recentActivity', {
    mapParameters: (() => {inViewer}),
    mapResponse: activity => ({activity})
})))

function aggregateStudents (students, group) {
  group.students.forEach(student => {
    if (!student) return
    const {studentId: id} = student
    const record = students[id] || (students[id] = {...student, groups: {}})
    record.groups[group.id] = group
  })
  return students
}

export function Groups (context) {
  const {groups, groupsLanguage, languageId} = context
  if (groups.length == 0 || groupsLanguage != languageId) {
    delayedSetState(0, fetch(context.role, 'myGroups', {
      mapParameters: ({languageId: language}) => ({language}),
      mapResponse: groups => {
        const students = groups.reduce(aggregateStudents,{})
        const myGrades = chain(groups)
          .map(({course, grade, precedence}) => ({id: course.substring(4, 7), name: grade, precedence}))
          .uniqBy('id')
          .sortBy('precedence')
          .value()
          const mySubjects = Object.values(groups.reduce((acc, group) => {
            const id = group.course.substring(0, 2)
            const grade = group.course.substring(4, 7)
            acc[id] = acc[id] || {id, name: group.subject, grades: {}}
            acc[id].grades[grade] = true
            return acc
          }, {}))
        return {students, groups, groupsLoaded: true, groupsLanguage: languageId, mySubjects, myGrades}
      }
    }))(context)
  }
}

export function StudentsDownloadLog (context) {
  const {page, section} = context
  if (page == 'monitoring' && section == 'students') {
    const loading = studentsDownloadLog => ({...context.loading, studentsDownloadLog})
    context.setState({loading: loading(true)})
    delayedSetState(0, fetch('teacher', 'studentsDownloadLog', {
      mapParameters: ({query, groupId}) => {
        return {...query, group: groupId}
      },
      mapResponse: studentsDownloadLog => ({studentsDownloadLog, loading: loading(false)})
    }))(context)
  }
}

export function DownloadLog (context) {
  const {page, section} = context
  if (page == 'monitoring' && section == 'student') {
    const loading = downloadLog => ({...context.loading, downloadLog})
    context.setState({loading: loading(true)})
    delayedSetState(0, fetch('teacher', 'downloadLog', {
      mapParameters: ({query, studentId, groupId: group}) => {
        return {...query, studentId, group}
      },
      mapResponse: downloadLog => ({downloadLog, loading: loading(false)})
    }))(context)
  }
}

function matchUnit(course) {
  return ({id}) => id.startsWith(course)
}

function matchLesson(unit) {
  return ({id}) => id.startsWith(unit)
}

export function DownloadLogFilters (context) {
  if (context.page == 'monitoring') {
    delayedSetState(0, fetch('teacher', 'downloadLogFilters', {
      mapParameters: () => ({}),
      mapResponse: data => {
        const [groupData, unitData, lessonData] = data
        if (groupData) {
          const units = unitData.map(unit => ({...unit, lessons: lessonData.filter(matchLesson(unit.id))}))
          const downloadLogFilters = groupData.map(group => ({...group, units: units.filter(matchUnit(group.course))}))
          const lessonLogTitles = lessonData.reduce((acc, lesson) => ({...acc, [lesson.id]: lesson}), {})
          return {downloadLogFilters, lessonLogTitles}
        }
      }
    }))(context)
  }
}

export function Events (context) {
  if (context.page == 'calendars') {
    delayedSetState(0, fetch('teacher', 'events', {
      mapParameters: ({query, languageId: language}) => {

        const start = query.start && moment(query.start).startOf('day').toISOString()
        const end = query.end && moment(query.end).endOf('day').toISOString()
        return {language, ...query, start, end}
      },
      mapResponse: events => {
        const eventsByDay = events.reduce((days, event) => {
          const start = moment(event.start)
          const end = moment(event.end)
          const dayDiff = Math.floor(moment.duration(end.diff(start)).as('days'))
          range(dayDiff + 1).forEach(i => {
            const day = moment(start).add(i,'days').format('YYYY-MM-DD') 
            days[day] = days[day] || [] 
            days[day].push(event)
          })
          return days
        }, {})
        return {eventsByDay, events}
      }
    }))(context)
  }
}

export function MyLessons (context) {
  const addingLessonsToPlan = context.page == 'planning' && context.view == 'plan' && context.activeTab == 'my-lessons'
  if (context.page == 'my-lessons' || addingLessonsToPlan) {
    const loading = myLessons => ({...context.loading, myLessons})
    context.setState({loading: loading(true)})
    delayedSetState(0, fetch('teacher','myLessons', {
      mapParameters: ({query,languageId: language}) => ({...query, language}),
      mapResponse: myLessons => ({myLessons, loading: loading(false)})
    }))(context)
  }
}

export function GroupsDigests (context) {
  if (context.page == 'rollbook') {
    const loading = groupsDigests => ({...context.loading, groupsDigests})
    context.setState({loading: loading(true)})
    const fetchGroupsDigests = fetch('teachers',`${context.profile.Passport.id}/groups`,{
      server: subdomainResource('assessment'),
      mapParameters: () => ({use_digest: true}),
      mapResponse: ({links}) => {
        const groupsDigests = links.reduce((acc, {href, digest}) => {
          const group = href.substring(54,90)
          acc[group] = digest
          return acc
        }, {})
        return {groupsDigests, loading: loading(false)}
      }
    })
    delayedSetState(0, fetchGroupsDigests)(context)
  }
}

export function Assessments (context) {
  if (context.page == 'assessment') {
    const {page = 1, pageSize = 8} = context.query
    const loading = assessments => ({...context.loading, assessments})
    context.setState({loading: loading(true)})
    const fetchAssessments = fetch('teachers',`${context.profile.Passport.id}/assessment_templates`,{
      server: subdomainResource('assessment'),
      mapParameters: ({query}) => ({use_digest: true, filter: query.pattern}),
      mapResponse: ({links}) => {
        const templates = links.filter(link => link.rel == 'assessment_templates')
        const chunks = chunk(templates, pageSize)
        const index = Math.min(page, chunks.length) - 1
        const assessments = chunks[index].map(({href, digest}) => ({id: href.substring(68,104), digest}))
        return {assessments, assessmentCount: templates.length, loading: loading(false)}
      }
    })
    delayedSetState(0, fetchAssessments)(context)
  }
}

export async function AssessmentView (context) {
  if (context.page == 'assessment' && context.assessments.length > 0) {
    const loading = assessmentView => ({...context.loading, assessmentView})
    context.setState({loading: loading(true)})
    const fetchAssessment = ({id}) =>
      fetch('teachers',`${context.profile.Passport.id}/assessment_templates/${id}`,{server: subdomainResource('assessment')})()
      .then(assessment => omit(assessment, ['links']))
    const assessmentView = await Promise.all(context.assessments.map(fetchAssessment))
    context.setState({assessmentView, loading: loading(false)})
  }
}
function setLoading (value) {
  return {loading: {$auto: {isLoading: {$set: value}}}}        
}

function inPlanning ({page}) {
  return ['planning', 'planning-rd'].indexOf(page) >= 0
}

export async function NewPlan (context) {
  const {planId, view} = context

  if ( inPlanning(context) && ['plan', 'plan-rd', 'preview', 'preview-rd'].indexOf(view) >= 0 ) {
    const isNew = !planId

    await context.updateState({planUpdated: {$set: false}, newPlan: {$set: {}}, ...setLoading(true)})
    const fetchNewPlan = fetch('teacher','plan',{
      mapParameters: ({query, suggestedDate, suggestedGroup}) => (isNew ? {...query, suggestedGroup, suggestedDate} : {...query, id: planId}),
      mapResponse: ([data]) => {
        if (data === undefined) return {newPlan: false, ...setLoading(false)}
        const items = data.items[0] === null || view == 'plan-rd'
          ? []
          : orderBy(data.items.map(item => {
            if (item.source == 'item') return item
            const {content} = item
            const sections = content?.sections?.map(createPlanSection)
            return {...item, content: {...content, sections},  ...setLoading(false)}
          }), ['order'])
        const newPlan = {...data, items}
        return {newPlan, loadPlan: false, planUpdated: true,  ...setLoading(false)}
      }
    })
    delayedSetState(30, fetchNewPlan)(context)
  }
}

export async function PlanTemplates (context) {
  if (inPlanning(context) && context.view == 'plan') {
    const fetchTemplates = fetch('teacher','planTemplates',{
      mapParameters: ({query}) => query,
      mapResponse: planTemplates => {
        const templs = planTemplates.filter((template) => !template.label.startsWith('rd-'))
        return {planTemplates: templs, loadPlan: false}
      }
    })
    delayedSetState(0, fetchTemplates)(context)
  }
}

export async function PlanRDTemplates (context) {
  if (inPlanning(context) && context.view == 'plan-rd') {
    const fetchTemplates = fetch('teacher','planTemplates',{
      mapParameters: ({query}) => query,
      mapResponse: planTemplates => {
        const rdTemplates = planTemplates.filter((template) => template.label.startsWith('rd-'))
        return {planTemplates: rdTemplates, loadPlan: false}
      }
    })
    delayedSetState(0, fetchTemplates)(context)
  }
}

export async function TemplateRDItems (context) {
  if (inPlanning(context) && context.view == 'plan-rd') {
    const fetchTemplateItems = fetch('planning','templateItems',{
      mapParameters: () => ({}),
      mapResponse: templateItems => ({templateItems, loadPlan: false})
    })
    delayedSetState(0, fetchTemplateItems)(context)
  }
}

export async function TemplateItems (context) {
  if (inPlanning(context) && context.view == 'plan') {
    const fetchTemplateItems = fetch('planning','templateItems',{
      mapParameters: () => ({}),
      mapResponse: templateItems => ({templateItems, loadPlan: false})
    })
    delayedSetState(0, fetchTemplateItems)(context)
  }
}

export function Plans (context) {
  const inCalendar = ['plan'].indexOf(context.view) < 0
  if (inPlanning(context) && inCalendar && context.query.start) {
    context.updateState({plansUpdated: {$set: false}, ...setLoading(true) })
    delayedSetState(0, fetch('teacher', 'planSchedule', {
      mapParameters: ({query, languageId: language}) => ({language, ...query}),
      mapResponse: source => {
        const plans = source.filter(plan => !plan.content.source ? true : plan.content.source === context.page)

        const plansByDay = plans.reduce((days, plan) => {
          const start = moment(plan.start)
          const finish = moment(plan.finish)
          range(moment.duration(finish.diff(start)).as('days') + 1).forEach(i => {
            const day = moment(start).add(i,'days').format('YYYY-MM-DD') 
            days[day] = days[day] || [] 
            days[day].push(plan)
          })
          return days
        }, {})
        return {plansByDay, plans, plansUpdated: true}
      }
    }))(context)
  }
}

function fetchDE (entity) {
  return context => {
    const {page, view, section} = context
    const addingStandards = page == 'planning' && view == 'plan' && section == 'add-standards'
    if (addingStandards) {
      delayedSetState(0, fetch('de', entity, {
        mapParameters: ({languageId: language, query}) => ({language, ...query}),
        mapResponse: data => ({[`de${entity}`]: data})
      }))(context)
    }
  }
}

export const DESubjects = fetchDE('Subjects')
export const DEGrades = fetchDE('Grades')
export const DEStandards = fetchDE('Standards')
export const DEExpectations = fetchDE('Expectations')

export function PdfPlans (context) {
  if (context.page == 'planning-pdf') {
    context.setState({pdfPlans: [], pdfPlansLoaded: false})
    delayedSetState(0, fetch('teacher','plans', {
      mapParameters: ({languageId: language, query}) => ({...query, language}),
      mapResponse: pdfPlans => ({pdfPlans, pdfPlansLoaded: true})
    }))(context)
  }
}

export function TeacherStandards (context) {
  if (context.page == 'planning-pdf' || (context.page == 'planning' && context.view == 'preview')) {
    context.setState({teacherStandards: {}, teacherStandardsLoaded: false})
    delayedSetState(0, fetch('teacher', 'standards', {
      mapParameters: ({planId}) => ({planId}),
      mapResponse: standards => {
        const teacherStandards = indexEditionParents(standards)
        return {teacherStandards, teacherStandardsLoaded: true}
      }
    }))(context)
  }
}

export function TeacherExpectations (context) {
  if (context.page == 'planning-pdf' || (context.page == 'planning' && context.view == 'preview')) {
    context.setState({teacherExpectations: {}, teacherExpectationsLoaded: false})
    delayedSetState(0, fetch('teacher', 'expectations', {
      mapParameters: ({planId}) => ({planId}),
      mapResponse: expectations => {
        const teacherExpectations = indexEditionParents(expectations)
        return {teacherExpectations, teacherExpectationsLoaded: true}
      }
    }))(context)
  }
}

export function PlanningWeek (context) {
  if (context.view == 'week' && context.query.start) {
    context.setState({planningWeek: context.query.start})
  }
}

const openfireDisabled = process.env.CONVERSE_DISABLED == 'true'

/*
export function OpenfireSettings (context) {
  if (!openfireDisabled && !context.openfireSettingsLoaded) {
    delayedSetState(0, fetch('openfire', 'settings', {
      mapParameters: () => ({}),
      mapResponse: settings => ({openfireSettings: handleSettings(settings), openfireSettingsLoaded: true})
    }))(context)
  }
}
*/

export async function dashboardVideos(context) {
  const {page, settings = {}} = context
  const { dashboardVideos } = settings
  if (!(['dashboard', 'videos'].includes(page))) return
  if (!dashboardVideos) return
  const loadVideos = (loading, videos) => ({dashboard: { loadingVideos: {$set: loading}, videos: {$set: videos}}})


  await context.updateState(loadVideos(true, []))
  const videos = await getVideos(dashboardVideos)
  context.updateState(loadVideos(false, videos))
}

export * from './quiz-fetch'
export * from './preferences-fetch'
export * from './rollbook-fetch'

